// Endpoint functions for the orders module. Each returns a parsed view
// model (camelCase). Throws `AppError` via the axios interceptor — never
// wraps in `Result<T>`.

import { adminApi } from '@core/api/httpClient'

import {
  parseOrderDetail,
  parseOrderList,
  parseCancelRejectedListing,
  parseOfferingOrders,
  parseVaspListingReadiness,
  parseVaspOrder,
  parseVaspProcessingStatusCounts,
} from './orderParser'
import type { CancelRejectedListingResult, OfferingOrders } from '../types/orderTypes'
import type {
  OrderDetailDto,
  OrderListDto,
  VaspOrderDto,
  VaspProcessingStatusCountsDto,
} from './orderContracts'
import { ORDERS_ENDPOINT_PATHS } from './ordersEndpointPaths'
import {
  VASP_QUEUE_PROCESSING_STATUSES,
  type OrderProcessingStatus,
} from '../constants/orderProcessingStatus'
import type {
  CancelOrderPayload,
  GetOrdersListParams,
  Order,
  OrderDetail,
  PendingApprovalQueue,
  PendingApprovalQueueParams,
  RejectOrderPayload,
  VaspListingReadiness,
  VaspOrder,
  VaspProcessingStatusCounts,
} from '../types/orderTypes'
import type { PaginatedResponse } from '@shared/types/pagination'

/** Strip empty params and join arrays as comma-separated strings (API requirement). */
function cleanParams(params: GetOrdersListParams): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      if (value.length > 0) out[key] = value.join(',')
    } else if (value !== null && value !== undefined && value !== '') {
      out[key] = value
    }
  }
  return out
}

export async function getOrdersList(
  params: GetOrdersListParams,
): Promise<PaginatedResponse<Order>> {
  const apiParams = {
    ...cleanParams(params),
    sort: params.sort || 'newest_first',
  }
  const { data } = await adminApi.get<OrderListDto>(ORDERS_ENDPOINT_PATHS.LIST, {
    params: apiParams,
  })
  return parseOrderList(data)
}

export async function getOrderDetails(orderId: string): Promise<OrderDetail> {
  const { data } = await adminApi.get<{ data?: OrderDetailDto } | OrderDetailDto>(
    ORDERS_ENDPOINT_PATHS.DETAIL(orderId),
  )
  const dto = (data as { data?: OrderDetailDto }).data ?? (data as OrderDetailDto)
  return parseOrderDetail(dto)
}

export async function rejectOrder(orderId: string, payload: RejectOrderPayload): Promise<void> {
  await adminApi.patch(ORDERS_ENDPOINT_PATHS.REJECT(orderId), payload)
}

export async function cancelOrder(orderId: string, payload: CancelOrderPayload): Promise<void> {
  await adminApi.patch(ORDERS_ENDPOINT_PATHS.CANCEL(orderId), payload)
}

export async function approveOrder(orderId: string): Promise<void> {
  await adminApi.patch(ORDERS_ENDPOINT_PATHS.APPROVE(orderId))
}

/** Register the order with the VASP provider. Returns the created vasp_orders row. */
export async function sendVaspOrder(orderId: string): Promise<VaspOrder> {
  const { data } = await adminApi.post<VaspOrderDto>(ORDERS_ENDPOINT_PATHS.VASP_ORDER(orderId))
  return parseVaspOrder(data)
}

/** Pull the live vendor status and apply it locally. Returns the refreshed row. */
export async function syncVaspOrder(orderId: string): Promise<VaspOrder> {
  const { data } = await adminApi.post<VaspOrderDto>(ORDERS_ENDPOINT_PATHS.VASP_ORDER_SYNC(orderId))
  return parseVaspOrder(data)
}

/** Finalise a fully funded order — completes the whole offering. */
export async function finaliseOrder(orderId: string): Promise<void> {
  await adminApi.patch(ORDERS_ENDPOINT_PATHS.FINALISE(orderId))
}

export interface VaspOrdersListParams {
  page?: number
  limit?: number
  selectedStatus?: OrderProcessingStatus | null
}

/**
 * The single seam for the queue's request shape.
 *
 * Both the endpoint function and the query key call this, so the key and the
 * payload cannot disagree — previously the key hardcoded four statuses while the
 * request sent five.
 */
export function buildVaspOrdersListParams(params: VaspOrdersListParams): GetOrdersListParams {
  return {
    page: params.page ?? 1,
    limit: params.limit ?? 20,
    orderStatus: ['approved'],
    orderProcessingStatus: params.selectedStatus
      ? [params.selectedStatus]
      : [...VASP_QUEUE_PROCESSING_STATUSES],
    sort: 'newest_first',
  }
}

export async function getVaspOrdersList(
  params: VaspOrdersListParams,
): Promise<PaginatedResponse<Order>> {
  return getOrdersList(buildVaspOrdersListParams(params))
}

/** One grouped rollup. Replaces five `?limit=1` list calls per mount. */
export async function getVaspProcessingStatusCounts(): Promise<VaspProcessingStatusCounts> {
  const { data } = await adminApi.get<VaspProcessingStatusCountsDto>(
    ORDERS_ENDPOINT_PATHS.VASP_STATUS_COUNTS,
  )
  return parseVaspProcessingStatusCounts(data)
}

/**
 * How many of an offering's orders are ready for token issuance (LST-04).
 *
 * A pure read. Ctrl-Alt refuses `register-trade` until every order on the
 * offering is ready, and since LST-04 admin-service refuses it too — so this
 * exists to let an operator see the shortfall before spending the call, and to
 * tell them what they are acknowledging when they override it.
 */
export async function getOfferingVaspListingReadiness(
  offeringId: string,
): Promise<VaspListingReadiness> {
  // Untyped `get` + `data?.data ?? data`: admin-service wraps some reads in a
  // `{ data }` envelope and returns others bare, and this endpoint is new
  // enough that both are plausible. The parser is total either way.
  const { data } = await adminApi.get(
    ORDERS_ENDPOINT_PATHS.OFFERING_VASP_LISTING_READINESS(offeringId),
  )
  return parseVaspListingReadiness(data?.data ?? data)
}

// ── Pending-approval queue ──────────────────────────────────────────────────

/** An order sits in the approval queue once its payment is matched. */
const PENDING_APPROVAL_ORDER_STATUS = 'paid'

const PENDING_APPROVAL_PAGE_SIZE = 100

// Hard stop on the sweep below. A queue deeper than this is an operational
// problem of its own, and the screen says so (`truncated`) rather than firing an
// unbounded fan-out of requests at the admin service.
const PENDING_APPROVAL_MAX_PAGES = 20

/**
 * The whole approval queue in one shot, so the screen can group it by offering.
 *
 * A per-offering row needs the offering's full order count and totals, and the
 * list endpoint has no rollup to ask for — a page of 20 rows can only ever show
 * a floor. So sweep: one request for page 1, then the remaining pages in
 * parallel off the page count it reports.
 *
 * `truncated` compares what we actually hold against the server's own total, so
 * it catches both the page cap above and a server-side cap on `limit`.
 */
export async function getPendingApprovalQueue(
  params: PendingApprovalQueueParams = {},
): Promise<PendingApprovalQueue> {
  const base: GetOrdersListParams = {
    orderStatus: [PENDING_APPROVAL_ORDER_STATUS],
    limit: PENDING_APPROVAL_PAGE_SIZE,
    orderType: params.orderType,
  }

  const firstPage = await getOrdersList({ ...base, page: 1 })
  const pageCount = Math.min(firstPage.pagination.totalPages || 1, PENDING_APPROVAL_MAX_PAGES)

  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) =>
      getOrdersList({ ...base, page: index + 2 }),
    ),
  )

  const orders = [firstPage, ...remainingPages].flatMap((page) => page.data)

  return {
    orders,
    totalItems: firstPage.pagination.totalItems,
    truncated: orders.length < firstPage.pagination.totalItems,
  }
}

// ── LST-08: unwinding a listing DLD rejected at trade registration ──────────

/**
 * Cancel a rejected listing and unwind every order behind it.
 *
 * DLD refused the registered trade, so no tokens were minted and Ctrl-Alt offers
 * no way back — the corrected raise is a new listing. This cancels each live
 * order with the provider and locally, returns its reserved capacity, raises a
 * PENDING refund for every order whose payment had settled, and closes the
 * offering as CANCELLED.
 *
 * Destructive and not undoable, which is why it is behind an explicit confirm.
 * It IS idempotent, so a re-run after a partial failure finishes the job rather
 * than paying twice.
 *
 * The response matters: `blockedOrders` is non-empty exactly when the provider
 * refused to release some orders, which means the raise is NOT fully unwound.
 */
export async function cancelRejectedVaspListing(
  offeringId: string,
  reason?: string,
): Promise<CancelRejectedListingResult> {
  const { data } = await adminApi.post(
    ORDERS_ENDPOINT_PATHS.CANCEL_REJECTED_LISTING(offeringId),
    reason ? { reason } : {},
  )
  return parseCancelRejectedListing(data?.data ?? data)
}

/**
 * Every order on one offering, with each order's title-deed state.
 *
 * A pure read backing the offering detail drawer. Returns every order whatever
 * its status — the totals it comes with count completed orders only, and having
 * both is what makes them reconcilable.
 */
export async function getOfferingOrders(offeringId: string): Promise<OfferingOrders> {
  const { data } = await adminApi.get(ORDERS_ENDPOINT_PATHS.OFFERING_ORDERS(offeringId))
  return parseOfferingOrders(data?.data ?? data)
}
